import {FC, useState} from 'react';
import {useCan} from 'src/permissions/hooks';
import {Box, Typography} from '@mui/material';
import {Add} from '@mui/icons-material';
import {FormattedMessage, useIntl} from 'react-intl';
import {Table} from 'src/components/tables/Table';
import {TableLayout} from 'src/components/tables/TableLayout';
import {EmptyTable} from 'src/components/tables/EmptyTable';
import {ResponsiveTable} from 'src/components/common/ResponsiveTable';
import {CardList} from 'src/components/common/CardList';
import {customInvoiceItemsApi} from 'src/api';
import {useAppDispatch} from 'src/app/hooks';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import {CreateCustomInvoiceItemRequest, CustomInvoiceItemDTO} from 'src/types';
import {
    CustomInvoiceItemFormType,
    initialCustomInvoiceItem,
} from 'src/components/forms/formData/customInvoiceItem';
import {CustomInvoiceItemsTableProps} from './CustomInvoiceItemsTable.types';
import {columnConfigCustomInvoiceItems} from './CustomInvoiceItemsTable.tableConfig';
import {addButtonSx, tableContainerSx, tableSx} from './CustomInvoiceItemsTable.sx';
import {CreateCustomInvoiceItemModal} from './CreateCustomInvoiceItemModal';
import {CustomInvoiceItemCard} from './CustomInvoiceItemCard';

export const CustomInvoiceItemsTable: FC<CustomInvoiceItemsTableProps> = ({userId, businessId}) => {
    const canCreateItem = useCan('customInvoiceItems.create');
    const canUpdateItem = useCan('customInvoiceItems.update');
    const canDeleteItem = useCan('customInvoiceItems.delete');
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<CustomInvoiceItemDTO | null>(null);

    const {data: items} = customInvoiceItemsApi.useGetCustomInvoiceItemsQuery(
        {businessId, userId},
        {skip: !businessId || !userId}
    );
    const [createTrigger] = customInvoiceItemsApi.useCreateCustomInvoiceItemMutation();
    const [updateTrigger] = customInvoiceItemsApi.useUpdateCustomInvoiceItemMutation();
    const [deleteTrigger] = customInvoiceItemsApi.useDeleteCustomInvoiceItemMutation();

    const rows = items ?? [];

    const handleOpenCreate = () => {
        setEditingItem(null);
        setIsModalOpen(true);
    };

    const handleOpenEdit = (item: CustomInvoiceItemDTO) => {
        setEditingItem(item);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
    };

    const handleSubmit = async (values: CustomInvoiceItemFormType) => {
        const priceInCents = Math.round(Number(values.price) * 100);
        const quantity = Math.round(Number(values.quantity));

        try {
            dispatch(preloaderChangeStatus(true));

            if (editingItem) {
                await updateTrigger({
                    id: editingItem.id,
                    data: {
                        businessId,
                        text: values.text || undefined,
                        textFrench: values.textFrench,
                        textDeutsch: values.textDeutsch || undefined,
                        price: priceInCents,
                        quantity,
                    },
                }).unwrap();
                NotifySuccess(intl.formatMessage({id: 'updatedSuccessfully'}));
            } else {
                const data: CreateCustomInvoiceItemRequest = {
                    businessId,
                    userId,
                    text: values.text || undefined,
                    textFrench: values.textFrench,
                    textDeutsch: values.textDeutsch || undefined,
                    price: priceInCents,
                    quantity,
                };
                await createTrigger(data).unwrap();
                NotifySuccess(intl.formatMessage({id: 'createdSuccessfully'}));
            }

            handleCloseModal();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data?.message ?? error.data?.error);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const handleDelete = async (id: number) => {
        try {
            dispatch(preloaderChangeStatus(true));
            await deleteTrigger({id, businessId}).unwrap();
            NotifySuccess(intl.formatMessage({id: 'deletedSuccessfully'}));
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data?.message ?? error.data?.error);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const modalInitialValues: CustomInvoiceItemFormType = editingItem
        ? {
            text: editingItem.text ?? '',
            textFrench: editingItem.textFrench ?? '',
            textDeutsch: editingItem.textDeutsch ?? '',
            price: (editingItem.price / 100).toFixed(2),
            quantity: editingItem.quantity,
        }
        : initialCustomInvoiceItem;

    const tableElement = (
        <Table
            rows={rows}
            // The row actions follow their own permissions: editing and deleting an item are
            // separate endpoints, and a role can hold one without the other.
            columns={columnConfigCustomInvoiceItems(
                canUpdateItem ? handleOpenEdit : undefined,
                canDeleteItem ? handleDelete : undefined,
            )}
            hideFooter
            getRowHeight={() => 'auto'}
            sx={tableSx}
        />
    );

    return (
        // Flush inside the relationships card: rows and an add-row footer, no second card.
        <TableLayout
            flush
            hideFooter
            footerPadded={false}
            footer={canCreateItem && (
                <Box
                    sx={addButtonSx}
                    onClick={handleOpenCreate}
                >
                    <Add sx={{mr: 1, fontSize: '1.25rem'}}/>
                    <Typography
                        variant="body2"
                        color="primary"
                    >
                        <FormattedMessage id="addInvoiceItem"/>
                    </Typography>
                </Box>
            )}
        >
            <ResponsiveTable
                desktopElement={tableElement}
                responsiveElement={
                    // The grid cannot follow the card's width on a phone, so the rows are the cards
                    // themselves. The empty case keeps the grid's own overlay.
                    rows.length ? (
                        <CardList>
                            {rows.map((item) => (
                                <CustomInvoiceItemCard
                                    key={item.id}
                                    item={item}
                                    onEdit={canUpdateItem ? handleOpenEdit : undefined}
                                    onDelete={canDeleteItem ? handleDelete : undefined}
                                />
                            ))}
                        </CardList>
                    ) : (
                        <EmptyTable/>
                    )
                }
            />

            <CreateCustomInvoiceItemModal
                open={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleSubmit}
                initialValues={modalInitialValues}
                title={editingItem ? 'editInvoiceItem' : 'addInvoiceItem'}
                submitText={editingItem ? 'update' : 'save'}
            />
        </TableLayout>
    );
};
